// Frontend API service for GRE Quant Practice
import {
  API_BASE_URL,
  getAuthHeaders,
  isTokenValid,
} from '../../../../utils/utils';

const API_ENDPOINT = `${API_BASE_URL}/api/gre/quant`;

// Cache for progress data to avoid excessive API calls
let progressCache = null;
let progressCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for session history
let sessionHistoryCache = null;
let sessionHistoryCacheTimestamp = null;
const SESSION_HISTORY_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// Cache for seen question IDs
let seenQuestionsCache = null;
let seenQuestionsCacheTimestamp = null;
const SEEN_QUESTIONS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for readiness analytics
let readinessCache = null;
let readinessCacheTimestamp = null;
const READINESS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Request deduplication - track in-flight requests
const pendingRequests = new Map();

const buildApiError = async (response) => {
  if (response?.status === 401) {
    return new Error('Authentication failed');
  }

  const errorData = await response.json().catch(() => ({}));
  return new Error(errorData.detail || `HTTP ${response.status}`);
};

const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user'));
  } catch (error) {
    return null;
  }
};

const getAuthenticatedHeaders = () => {
  if (!isTokenValid()) {
    throw new Error('Authentication failed');
  }

  return getAuthHeaders();
};

/**
 * Save a completed practice session to the backend
 * @param {Object} practiceData - Practice session data
 * @returns {Promise<Object>} Response from backend
 */
export const savePracticeCompletion = async (practiceData) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    // Prepare practice data for backend
    const payload = {
      lesson_id: practiceData.lessonId,
      lesson_name: practiceData.lessonName,
      practice_mode: practiceData.practiceMode, // 'timed' or 'untimed'
      total_questions: practiceData.totalQuestions,
      correct_answers: practiceData.correctAnswers,
      total_time_seconds: practiceData.totalTimeSeconds,
      session_start_time: practiceData.sessionStartTime,
      session_end_time: practiceData.sessionEndTime,
      questions_data: practiceData.questionsData || {},
      device_info: practiceData.deviceInfo || navigator.userAgent,
      user_email: user.email,
    };

    console.log('Saving practice completion:', payload);

    const response = await fetch(
      `${API_ENDPOINT}/practice/complete?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'POST',
        headers: getAuthenticatedHeaders(),
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();

    // Clear progress cache when new completion is saved
    clearProgressCache();

    console.log('Practice completion saved successfully:', result);
    return result;
  } catch (error) {
    console.error('Error saving practice completion:', error);
    throw error;
  }
};

/**
 * Get comprehensive progress data for the current user
 * Uses caching and request deduplication to prevent excessive API calls
 * @param {boolean} forceRefresh - Force refresh of cached data
 * @returns {Promise<Object>} User progress data
 */
export const getUserProgress = async (forceRefresh = false) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const cacheKey = `progress_${user.email}`;

    // Return cached data if available and not expired
    if (!forceRefresh && progressCache && progressCacheTimestamp) {
      const now = Date.now();
      if (now - progressCacheTimestamp < CACHE_DURATION) {
        console.log('Returning cached progress data');
        return progressCache;
      }
    }

    // Check if there's already a pending request for this
    if (pendingRequests.has(cacheKey)) {
      console.log('Reusing pending request for progress data');
      return pendingRequests.get(cacheKey);
    }

    console.log('Fetching user progress from backend...');

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/progress?userEmail=${encodeURIComponent(user.email)}`,
          {
            method: 'GET',
            headers: getAuthenticatedHeaders(),
          }
        );

        if (!response.ok) {
          throw await buildApiError(response);
        }

        const result = await response.json();

        // Cache the result
        progressCache = result;
        progressCacheTimestamp = Date.now();

        console.log('User progress fetched successfully:', result);
        return result;
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    pendingRequests.set(cacheKey, fetchPromise);

    return fetchPromise;
  } catch (error) {
    console.error('Error fetching user progress:', error);
    throw error;
  }
};

/**
 * Get detailed statistics for a specific lesson
 * @param {string} lessonId - Lesson identifier
 * @returns {Promise<Object>} Lesson statistics
 */
export const getLessonStatistics = async (lessonId) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    console.log(`Fetching statistics for lesson: ${lessonId}`);

    const response = await fetch(
      `${API_ENDPOINT}/lesson/${lessonId}/stats?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'GET',
        headers: getAuthenticatedHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();
    console.log('Lesson statistics fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching lesson statistics:', error);
    throw error;
  }
};

/**
 * Get a summary of all lessons and their completion status
 * @returns {Promise<Object>} Lessons summary
 */
export const getLessonsSummary = async () => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    console.log('Fetching lessons summary...');

    const response = await fetch(
      `${API_ENDPOINT}/lessons/summary?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'GET',
        headers: getAuthenticatedHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();
    console.log('Lessons summary fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching lessons summary:', error);
    throw error;
  }
};

/**
 * Clear cached progress data
 */
export const clearProgressCache = () => {
  progressCache = null;
  progressCacheTimestamp = null;
};

export const clearReadinessCache = () => {
  readinessCache = null;
  readinessCacheTimestamp = null;
};

/**
 * Clear cached session history data
 */
export const clearSessionHistoryCache = () => {
  sessionHistoryCache = null;
  sessionHistoryCacheTimestamp = null;
};

export const clearSeenQuestionsCache = () => {
  seenQuestionsCache = null;
  seenQuestionsCacheTimestamp = null;
};

/**
 * Clear all caches
 */
export const clearAllCaches = () => {
  clearProgressCache();
  clearSessionHistoryCache();
  clearReadinessCache();
  clearSeenQuestionsCache();
};

/**
 * Reset all progress data for the current user (admin/debug functionality)
 * @param {boolean} confirm - Confirmation flag
 * @returns {Promise<Object>} Reset result
 */
export const resetUserProgress = async (confirm = false) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    if (!confirm) {
      throw new Error('Reset operation requires confirmation');
    }

    console.log('Resetting user progress...');

    const response = await fetch(
      `${API_ENDPOINT}/progress/reset?userEmail=${encodeURIComponent(user.email)}&confirm=true`,
      {
        method: 'DELETE',
        headers: getAuthenticatedHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();

    // Clear cache after reset
    clearProgressCache();

    console.log('User progress reset successfully:', result);
    return result;
  } catch (error) {
    console.error('Error resetting user progress:', error);
    throw error;
  }
};

/**
 * Helper function to save practice completion with proper error handling and fallback
 * This function can be used directly in lesson components
 */
export const saveQuantPracticeCompletion = async (lessonData) => {
  try {
    // Map lesson data to expected format
    const practiceData = {
      lessonId: lessonData.lessonId,
      lessonName:
        lessonData.lessonName || getLessonNameById(lessonData.lessonId),
      practiceMode: lessonData.practiceMode || 'untimed',
      totalQuestions: lessonData.totalQuestions || 0,
      correctAnswers: lessonData.correctAnswers || 0,
      totalTimeSeconds: lessonData.timeSpent
        ? Math.round(lessonData.timeSpent / 1000)
        : null,
      sessionStartTime: lessonData.sessionStartTime || new Date().toISOString(),
      sessionEndTime: new Date().toISOString(),
      questionsData: lessonData.questionsData || {},
      deviceInfo: navigator.userAgent,
    };

    // Try to save to backend
    const result = await savePracticeCompletion(practiceData);

    // Also save to localStorage as backup
    saveToLocalStorageBackup(lessonData);

    return result;
  } catch (error) {
    console.warn('Backend save failed, falling back to localStorage:', error);

    // Fallback to localStorage if backend fails
    saveToLocalStorageBackup(lessonData);

    return {
      success: true,
      message: 'Saved to local storage (backend unavailable)',
      fallback: true,
    };
  }
};

/**
 * Fallback function to save to localStorage
 */
const saveToLocalStorageBackup = (lessonData) => {
  try {
    const existingProgress = JSON.parse(
      localStorage.getItem('concepts_mastery_progress') || '[]'
    );
    const filteredProgress = existingProgress.filter(
      (lesson) => lesson.lessonId !== lessonData.lessonId
    );

    filteredProgress.push({
      lessonId: lessonData.lessonId,
      score: lessonData.score,
      completedAt: new Date().toISOString(),
      timeSpent: lessonData.timeSpent || 0,
      practiceMode: lessonData.practiceMode || 'untimed',
    });

    localStorage.setItem(
      'concepts_mastery_progress',
      JSON.stringify(filteredProgress)
    );
    console.log('Practice completion saved to localStorage as backup');
  } catch (error) {
    console.error('Error saving to localStorage backup:', error);
  }
};

/**
 * Helper function to get lesson name by ID
 */
const getLessonNameById = (lessonId) => {
  const lessonNames = {
    integers: 'Integers & Number Properties',
    'percentage-ratio': 'Percentages & Ratios',
    algebra: 'Algebra & Functions',
    'coordinate-geometry': 'Coordinate Geometry',
    circles: 'Circles & Cylinders',
    triangles: 'Triangles',
    quadrilaterals: 'Quadrilaterals',
    polygons: 'Polygons',
    'three-dimensional-figures': '3D Figures',
    statistics: 'Statistics',
    volumes: 'Volumes & Surface Area',
    graphs: 'Data Analysis & Graphs',
  };

  return lessonNames[lessonId] || lessonId;
};

/**
 * Check backend health
 * @returns {Promise<Object>} Health status
 */
export const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${API_ENDPOINT}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw await buildApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Backend health check failed:', error);
    throw error;
  }
};

/**
 * Save a practice session to the backend history
 * @param {Object} sessionData - Session data including questions, answers, results
 * @returns {Promise<Object>} Response from backend
 */
export const savePracticeSessionHistory = async (sessionData) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const payload = {
      session_id: sessionData.id,
      timestamp: sessionData.timestamp,
      config: sessionData.config,
      questions: sessionData.questions,
      answers: sessionData.answers,
      question_times: sessionData.questionTimes,
      results: sessionData.results,
      flagged_questions: sessionData.flaggedQuestions,
      stimuli: sessionData.stimuli || {},
      user_email: user.email,
    };

    console.log('Saving practice session to backend:', payload.session_id);

    const response = await fetch(
      `${API_ENDPOINT}/practice/session?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'POST',
        headers: getAuthenticatedHeaders(),
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();

    // Clear session history cache so next fetch gets fresh data
    sessionHistoryCache = null;
    sessionHistoryCacheTimestamp = null;
    clearSeenQuestionsCache();

    console.log('Practice session saved successfully:', result);
    return result;
  } catch (error) {
    console.error('Error saving practice session:', error);
    throw error;
  }
};

/**
 * Get practice session history from the backend
 * Uses caching and request deduplication to prevent excessive API calls
 * @param {number} limit - Maximum number of sessions to retrieve
 * @param {boolean} forceRefresh - Force refresh of cached data
 * @returns {Promise<Array>} Array of session history
 */
export const getPracticeSessionHistory = async (
  limit = 50,
  forceRefresh = false
) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const cacheKey = `sessions_${user.email}_${limit}`;

    // Return cached data if available and not expired
    if (!forceRefresh && sessionHistoryCache && sessionHistoryCacheTimestamp) {
      const now = Date.now();
      if (now - sessionHistoryCacheTimestamp < SESSION_HISTORY_CACHE_DURATION) {
        console.log('Returning cached session history');
        return sessionHistoryCache;
      }
    }

    // Check if there's already a pending request for this
    if (pendingRequests.has(cacheKey)) {
      console.log('Reusing pending request for session history');
      return pendingRequests.get(cacheKey);
    }

    console.log('Fetching practice session history from backend...');

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/practice/sessions?userEmail=${encodeURIComponent(user.email)}&limit=${limit}`,
          {
            method: 'GET',
            headers: getAuthenticatedHeaders(),
          }
        );

        if (!response.ok) {
          throw await buildApiError(response);
        }

        const result = await response.json();

        // Cache the result
        sessionHistoryCache = result;
        sessionHistoryCacheTimestamp = Date.now();

        console.log(
          'Practice session history fetched successfully:',
          result.length,
          'sessions'
        );
        return result;
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    pendingRequests.set(cacheKey, fetchPromise);

    return fetchPromise;
  } catch (error) {
    console.error('Error fetching practice session history:', error);
    throw error;
  }
};

/**
 * Delete a practice session from the backend
 * @param {string} sessionId - Session ID to delete
 * @returns {Promise<Object>} Response from backend
 */
export const deletePracticeSession = async (sessionId) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const response = await fetch(
      `${API_ENDPOINT}/practice/session/${sessionId}?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'DELETE',
        headers: getAuthenticatedHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();
    clearSessionHistoryCache();
    clearSeenQuestionsCache();
    return result;
  } catch (error) {
    console.error('Error deleting practice session:', error);
    throw error;
  }
};

/**
 * Get persistent seen GRE Quant question IDs from the backend
 * @param {boolean} forceRefresh - Force refresh of cached data
 * @returns {Promise<string[]>}
 */
export const getSeenQuestionIdsFromBackend = async (forceRefresh = false) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const cacheKey = `seen_${user.email}`;

    if (
      !forceRefresh &&
      seenQuestionsCache &&
      seenQuestionsCacheTimestamp &&
      Date.now() - seenQuestionsCacheTimestamp < SEEN_QUESTIONS_CACHE_DURATION
    ) {
      return seenQuestionsCache;
    }

    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/practice/seen-questions?userEmail=${encodeURIComponent(user.email)}`,
          {
            method: 'GET',
            headers: getAuthenticatedHeaders(),
          }
        );

        if (!response.ok) {
          throw await buildApiError(response);
        }

        const result = await response.json();
        const questionIds = Array.isArray(result.question_ids)
          ? result.question_ids
          : [];
        seenQuestionsCache = questionIds;
        seenQuestionsCacheTimestamp = Date.now();
        return questionIds;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  } catch (error) {
    console.error('Error fetching seen question IDs:', error);
    throw error;
  }
};

/**
 * Sync answered GRE Quant question IDs to the backend seen registry
 * @param {string[]} questionIds
 * @returns {Promise<Object>}
 */
export const syncSeenQuestionIds = async (questionIds = []) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    if (!Array.isArray(questionIds) || questionIds.length === 0) {
      return { success: true, synced: 0, question_ids: [] };
    }

    const response = await fetch(
      `${API_ENDPOINT}/practice/seen-questions/sync?userEmail=${encodeURIComponent(user.email)}`,
      {
        method: 'POST',
        headers: getAuthenticatedHeaders(),
        body: JSON.stringify({
          question_ids: questionIds,
          user_email: user.email,
        }),
      }
    );

    if (!response.ok) {
      throw await buildApiError(response);
    }

    const result = await response.json();
    seenQuestionsCache = Array.isArray(result.question_ids)
      ? result.question_ids
      : questionIds;
    seenQuestionsCacheTimestamp = Date.now();
    return result;
  } catch (error) {
    console.error('Error syncing seen question IDs:', error);
    throw error;
  }
};

/**
 * Get comprehensive readiness analytics for GRE Quant
 * @param {boolean} forceRefresh - Force refresh of cached data
 * @returns {Promise<Object>} Readiness analytics data
 */
export const getQuantReadiness = async (forceRefresh = false) => {
  try {
    const user = getSessionUser();
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const cacheKey = `readiness_${user.email}`;

    if (!forceRefresh && readinessCache && readinessCacheTimestamp) {
      if (Date.now() - readinessCacheTimestamp < READINESS_CACHE_DURATION) {
        return readinessCache;
      }
    }

    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/readiness?userEmail=${encodeURIComponent(user.email)}`,
          {
            method: 'GET',
            headers: getAuthenticatedHeaders(),
          }
        );

        if (!response.ok) {
          throw await buildApiError(response);
        }

        const result = await response.json();
        readinessCache = result;
        readinessCacheTimestamp = Date.now();
        return result;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  } catch (error) {
    console.error('Error fetching readiness analytics:', error);
    throw error;
  }
};
